import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Paper,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
// Import our debugging utility
import { createLogger } from '../utils/debugUtils';

/**
 * SelectColumns component - allows users to select which columns to apply filtering to
 */
const SelectColumns = ({
  onNext,
  onBack,
  fileData,
  selectedColumns,
  setSelectedColumns,
  setFileData,
  setFilterParams,
  setFilterConfig
}) => {
  // Create component logger
  const logger = createLogger('SelectColumns');
  logger.lifecycle('Component mounted');
  logger.info('Initial props', { fileData, selectedColumns });
  
  // Store previous values for comparison
  const prevSelectedColumnsRef = useRef(selectedColumns);
  
  const [search, setSearch] = useState('');
  const [filteredColumns, setFilteredColumns] = useState([]);
  const [error, setError] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  // Filter columns based on search input
  useEffect(() => {
    logger.debug('useEffect [search, fileData] triggered', { search, fileData });
    
    if (!fileData?.columns) {
      logger.debug('No columns found in fileData, setting filteredColumns to empty array');
      setFilteredColumns([]);
      return;
    }
    
    const filtered = fileData.columns.filter(col => 
      col.toLowerCase().includes(search.toLowerCase())
    );
    logger.debug('Filtered columns based on search', { 
      searchTerm: search,
      totalColumns: fileData.columns.length,
      filteredCount: filtered.length,
      filtered
    });
    
    setFilteredColumns(filtered);
  }, [search, fileData, logger]);

  // Handle column selection toggle
  const handleToggle = (column) => () => {
    logger.debug(`Toggle column selection for: ${column}`);
    const currentIndex = selectedColumns.indexOf(column);
    const newSelectedColumns = [...selectedColumns];

    if (currentIndex === -1) {
      logger.debug(`Adding column: ${column}`);
      newSelectedColumns.push(column);
    } else {
      logger.debug(`Removing column: ${column}`);
      newSelectedColumns.splice(currentIndex, 1);
    }

    logger.info('Setting new selected columns', { 
      column,
      action: currentIndex === -1 ? 'added' : 'removed',
      before: selectedColumns,
      after: newSelectedColumns
    });
    
    setSelectedColumns(newSelectedColumns);
  };

  // Handle select all columns
  const handleSelectAll = () => {
    logger.debug('Select all columns button clicked');
    if (fileData?.columns) {
      logger.info('Setting all filtered columns as selected', { 
        count: filteredColumns.length,
        columns: filteredColumns
      });
      setSelectedColumns(filteredColumns);
    }
  };

  // Handle deselect all columns
  const handleDeselectAll = () => {
    logger.debug('Deselect all columns button clicked');
    logger.info('Clearing all selected columns', { 
      before: selectedColumns
    });
    setSelectedColumns([]);
  };

  // Handle proceeding to next step
  const handleNext = () => {
    logger.debug('Next button clicked');
    
    if (!selectedColumns.length) {
      logger.warn('No columns selected, showing error');
      setError('Please select at least one column to filter.');
      return;
    }
    
    logger.info('Proceeding to next step with columns', {
      selectedColumns,
      count: selectedColumns.length
    });
    
    // Check for localStorage values that might override our selection
    const selectedColumnsInStorage = localStorage.getItem('selectedColumns');
    const extractedColumnsInStorage = localStorage.getItem('extractedColumns');
    logger.debug('Checking localStorage for potential overrides', {
      selectedColumnsInStorage: selectedColumnsInStorage ? 'exists' : 'not found',
      extractedColumnsInStorage: extractedColumnsInStorage ? 'exists' : 'not found',
    });
    
    setError(null);
    onNext();
  };

  // Handle resetting all data
  const handleReset = () => {
    logger.debug('Reset button clicked, showing confirmation dialog');
    setResetDialogOpen(true);
  };

  // Confirm and execute reset
  const confirmReset = () => {
    logger.info('Resetting all application state');
    
    // Reset all application state
    setSelectedColumns([]);
    
    // Initialize filterParams properly instead of setting to null
    setFilterParams && setFilterParams({
      model: 'o4-mini-high',
      reasoningText: '',
      chunkSize: 300
    });
    
    setFilterConfig && setFilterConfig({});
    setFileData && setFileData(null);
    setResetDialogOpen(false);
    
    // Also clear localStorage items that might interfere
    logger.debug('Clearing localStorage items that might affect column selection');
    try {
      localStorage.removeItem('selectedColumns');
      localStorage.removeItem('extractedColumns');
    } catch (e) {
      logger.error('Error clearing localStorage', e);
    }
    
    // Navigate back to upload screen
    onBack();
    logger.info('All data has been reset, navigating back');
  };

  // Cancel reset
  const cancelReset = () => {
    logger.debug('Reset cancelled by user');
    setResetDialogOpen(false);
  };
  
  // Track changes to selectedColumns prop for debugging
  useEffect(() => {
    const prevSelected = prevSelectedColumnsRef.current;
    
    // Only log if the value has changed
    if (JSON.stringify(prevSelected) !== JSON.stringify(selectedColumns)) {
      logger.info('selectedColumns prop changed', {
        prev: prevSelected,
        current: selectedColumns,
        added: selectedColumns.filter(col => !prevSelected.includes(col)),
        removed: prevSelected.filter(col => !selectedColumns.includes(col))
      });
      
      // Update ref
      prevSelectedColumnsRef.current = selectedColumns;
    }
  }, [selectedColumns, logger]);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header removed to avoid redundancy with page title */}
      <Typography variant="body1" sx={{ mb: 3 }}>
        Pick the fields you'll filter later.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper variant="outlined" sx={{ p: 3, mb: 3, bgcolor: '#161c3e', borderColor: 'rgba(255, 255, 255, 0.1)' }}>
        {fileData?.columns && fileData.columns.length > 0 ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <TextField
                placeholder="Search fields..."
                variant="outlined"
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ width: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Box>
                <Button onClick={handleSelectAll} sx={{ mr: 1 }}>
                  Select All
                </Button>
                <Button onClick={handleDeselectAll}>
                  Deselect All
                </Button>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ 
              height: '400px', 
              overflow: 'auto',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: 1
            }}>
              <List sx={{ width: '100%' }}>
                {filteredColumns.map((column) => {
                  const labelId = `checkbox-list-label-${column}`;
                  return (
                    <ListItem
                      key={column}
                      dense
                      button
                      onClick={handleToggle(column)}
                      sx={{
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                        backgroundColor: selectedColumns.indexOf(column) !== -1 ? 'rgba(67, 97, 238, 0.08)' : 'inherit',
                      }}
                    >
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={selectedColumns.indexOf(column) !== -1}
                          tabIndex={-1}
                          disableRipple
                          inputProps={{ 'aria-labelledby': labelId }}
                          color="primary"
                        />
                      </ListItemIcon>
                      <ListItemText id={labelId} primary={column} />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="textSecondary">
                {selectedColumns.length} of {fileData.columns.length} fields selected
              </Typography>
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="textSecondary" align="center">
            No columns available. Please upload a valid CSV file.
          </Typography>
        )}
      </Paper>
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button 
          onClick={onBack}
          variant="outlined" 
          sx={{ 
            borderColor: 'rgba(255,255,255,0.3)',
            color: 'text.secondary',
            textTransform: 'none',
            borderRadius: '4px'  
          }}
        >
          Previous
        </Button>
        
        {/* New Reset All Data button */}
        <Button 
          variant="outlined" 
          color="secondary"
          onClick={handleReset}
          startIcon={<RefreshIcon />}
          sx={{ 
            textTransform: 'none',
            borderRadius: '4px'
          }}
        >
          Reset
        </Button>
        
        <Button 
          variant="contained" 
          color="primary"
          onClick={handleNext} 
          disabled={!selectedColumns.length}
          sx={{ 
            minWidth: 100,
            textTransform: 'none', 
            borderRadius: '4px',
            fontWeight: 'medium',
            px: 3
          }}
        >
          Next
        </Button>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={cancelReset}
        aria-labelledby="reset-dialog-title"
        aria-describedby="reset-dialog-description"
      >
        <DialogTitle id="reset-dialog-title">
          Reset?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="reset-dialog-description">
            This will clear all your selections and uploaded data. You'll need to start over from the beginning. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelReset} color="primary">
            Cancel
          </Button>
          <Button onClick={confirmReset} color="error" variant="contained">
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Add logging on component unmount using a cleanup function
const SelectColumnsWithDebug = (props) => {
  const instance = SelectColumns(props);
  
  // Add useEffect for cleanup
  React.useEffect(() => {
    return () => {
      const logger = createLogger('SelectColumns');
      logger.lifecycle('Component unmounted');
    };
  }, []);
  
  return instance;
};

export default SelectColumnsWithDebug;
